/**
 * SSOT — סיכום מחיר מלון (מחיר ספק במטבע API, שער נעול, מלון בש״ח, דמי טיפול, סה״כ).
 */

import {
  isKnownHotelSearchCurrencyCode,
  normalizeHotelSearchCurrencyCode,
} from "../constants/hotelCurrencies";
import { roundMoney2 } from "../pricing/pricingEngine";

function normalizeSupplierCurrencyForBooking(code) {
  const c = String(code ?? "")
    .trim()
    .toUpperCase();
  if (!c) return "USD";
  if (c === "ILS" || c === "EUR" || c === "GBP" || c === "AED") return c;
  if (isKnownHotelSearchCurrencyCode(c)) return c;
  return normalizeHotelSearchCurrencyCode(c);
}
import {
  getHotelProfilePackageSupplierUsd,
  resolveHotelProfilePackageRawCurrency,
} from "./hotelSellingPrice";
import { resolveHotelPackageDisplayCurrencies } from "./resolveHotelPackageDisplayCurrencies";
import { resolveHotelProfilePackageBookingSsot } from "./agentHotelProfilePricing";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "./hotelBookingPriceSsot";
import { isAgentHrCustomerFacingSsotOrder } from "./resolvePassengerHotelCustomerFacingDisplay";
import { resolveHotelAgentHandlingFeeIls } from "./resolveHotelAgentOfferDisplayTotal";
import { resolveHotelHandlingFeeSnapshotSsot } from "./orders/resolveHotelHandlingFeeSnapshotSsot";
import { resolveProfileUsdToIlsExchangeRate } from "./resolveHotelRoomPackageDisplayPrice";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";
import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { isHotelApiPriceOnlyOrder } from "./hotelApiPriceOnlyPipeline";

export const HOTEL_BOOKING_SUMMARY_PRICE_SSOT_TRACE =
  "[HOTEL_BOOKING_SUMMARY_PRICE_SSOT_TRACE]";
export const HOTEL_AGENT_FEE_SUMMARY_TOTAL_TRACE =
  "[HOTEL_AGENT_FEE_SUMMARY_TOTAL_TRACE]";
export const HOTEL_LOCKED_FX_RATE_TRACE = "[HOTEL_LOCKED_FX_RATE_TRACE]";

function roundIls(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.ceil(x);
}

/** Agent HR booking SSOT keeps exact money2 charge; B2C legacy paths still ceil. */
function roundBookingSsotIlsForSnapshot(bookingSsotIls, bookingSsotTrace) {
  const pipeline = String(bookingSsotTrace?.pipeline ?? bookingSsotTrace?.source ?? "");
  if (pipeline.includes("agent_hr")) {
    return roundMoney2(bookingSsotIls);
  }
  return roundIls(bookingSsotIls);
}

function roundRate(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return null;
  return Math.round(x * 10000) / 10000;
}

function finitePositive(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Admin FX only — never ILS÷supplier USD.
 * @param {string} supplierCurrency
 * @param {{
 *   currencyRate?: { USD?: number, EUR?: number, GBP?: number }|null,
 *   adminUsdRate?: number|null,
 *   adminEurRate?: number|null,
 *   adminGbpRate?: number|null,
 *   profilePriceContext?: object|null,
 *   usdToIls?: ((usd: number, fixed?: boolean) => number)|null,
 * }} [rateOpts]
 */
export function resolveAdminHotelLockedExchangeRateToIls(
  supplierCurrency,
  rateOpts = {}
) {
  const cur = normalizeSupplierCurrencyForBooking(supplierCurrency);
  if (cur === "ILS") return 1;

  const {
    currencyRate,
    adminUsdRate,
    adminEurRate,
    adminGbpRate,
    profilePriceContext,
    usdToIls,
  } = rateOpts;

  if (cur === "USD") {
    const explicit = finitePositive(adminUsdRate);
    if (explicit != null) return roundRate(explicit);
    const fromCurrencyRate = finitePositive(currencyRate?.USD);
    if (fromCurrencyRate != null) return roundRate(fromCurrencyRate);
    if (typeof usdToIls === "function") {
      try {
        const one = usdToIls(1, false);
        const n = finitePositive(one);
        if (n != null) return roundRate(n);
      } catch {
        /* ignore */
      }
    }
    const profileCtx =
      profilePriceContext ??
      (typeof usdToIls === "function" ? { usdToIls } : null);
    const directProfileRate = finitePositive(
      profileCtx?.usdToIlsRate ??
        profileCtx?.exchangeRate ??
        profileCtx?.currencyRate?.USD
    );
    if (directProfileRate != null) return roundRate(directProfileRate);
    const fromProfile = resolveProfileUsdToIlsExchangeRate(profileCtx);
    if (fromProfile != null) return roundRate(fromProfile);
    return null;
  }

  if (cur === "EUR") {
    const explicit = finitePositive(adminEurRate);
    if (explicit != null) return roundRate(explicit);
    const fromCurrencyRate = finitePositive(currencyRate?.EUR);
    if (fromCurrencyRate != null) return roundRate(fromCurrencyRate);
    const fromProfile = finitePositive(profilePriceContext?.eurToIls);
    return fromProfile != null ? roundRate(fromProfile) : null;
  }

  if (cur === "GBP") {
    const explicit = finitePositive(adminGbpRate);
    if (explicit != null) return roundRate(explicit);
    const fromCurrencyRate = finitePositive(currencyRate?.GBP);
    if (fromCurrencyRate != null) return roundRate(fromCurrencyRate);
    const fromProfile = finitePositive(profilePriceContext?.gbpToIls);
    return fromProfile != null ? roundRate(fromProfile) : null;
  }

  return null;
}

function buildHotelLockedFxRateOpts(opts = {}) {
  return {
    currencyRate: opts.currencyRate ?? null,
    adminUsdRate: opts.adminUsdRate ?? null,
    adminEurRate: opts.adminEurRate ?? null,
    adminGbpRate: opts.adminGbpRate ?? null,
    profilePriceContext: opts.profilePriceContext ?? null,
    usdToIls: opts.usdToIls ?? null,
  };
}

function computeImpliedHotelFxRate(hotelTotalIls, supplierAmount) {
  const ils = roundIls(hotelTotalIls);
  const supplier = finitePositive(supplierAmount);
  if (ils <= 0 || supplier == null) return null;
  return roundRate(ils / supplier);
}

function pickSnapshot(order) {
  if (!order || typeof order !== "object") return null;
  const snap =
    order.pricingSnapshot ??
    order.selectedPriceSnapshot?.pricingSnapshot ??
    null;
  return snap && typeof snap === "object" ? snap : null;
}

function pickBookingSsotTrace(order) {
  if (!order || typeof order !== "object") return null;
  return (
    order.bookingSsotTrace ??
    order.selectedPriceSnapshot?.bookingSsotTrace ??
    null
  );
}

/**
 * Hotel line ILS for summary/payment — agent HR booking SSOT wins over stale pricingSnapshot.
 * @param {object|null|undefined} order
 * @returns {number}
 */
export function resolveSummaryHotelTotalIlsFromOrder(order) {
  if (!order || typeof order !== "object") return 0;

  const bookingSsotIls = resolveCustomerFacingHotelLineIlsFromOrder(order);
  if (isAgentHrCustomerFacingSsotOrder(order) && bookingSsotIls != null) {
    return roundBookingSsotIlsForSnapshot(bookingSsotIls, pickBookingSsotTrace(order));
  }

  const lockedSnapRaw = pickSnapshot(order);
  if (lockedSnapRaw?.hotelTotalIls != null) {
    return roundIls(lockedSnapRaw.hotelTotalIls);
  }

  return bookingSsotIls ?? 0;
}

/** מספר מ-displayCurrencies — כולל formattedUsd כמו בתצוגה הכפולה (HotelApiPriceDualLineDisplay). */
function parseDisplayCurrencyAmount(dc, currency) {
  if (!dc || typeof dc !== "object") return null;
  const cur = String(currency || "USD").toUpperCase();
  if (cur === "USD") {
    const raw = dc.usd;
    if (raw != null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const fmt = dc.formattedUsd;
    if (fmt != null && String(fmt).trim() !== "") {
      const m = String(fmt).replace(/[^\d.,]/g, "").replace(/,/g, "");
      const n = Number(m);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }
  if (cur === "EUR") {
    const raw = dc.eur ?? dc.EUR;
    if (raw != null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const fmt = dc.formattedEur ?? dc.formattedEUR;
    if (fmt != null && String(fmt).trim() !== "") {
      const m = String(fmt).replace(/[^\d.,]/g, "").replace(/,/g, "");
      const n = Number(m);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

function resolveOrderDisplayCurrencies(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  const fromPkg =
    pkg.displayCurrencies && typeof pkg.displayCurrencies === "object"
      ? pkg.displayCurrencies
      : null;
  const resolved = resolveHotelPackageDisplayCurrencies(pkg);
  if (!fromPkg && !resolved) return null;
  return { ...resolved, ...(fromPkg || {}) };
}

/**
 * מסק שער/מטבע מ-snapshot קיים או מ-displayCurrencies + bookingSsotIls (הזמנות ישנות / api-only ללא snapshot מלא).
 * @param {object|null|undefined} order
 * @param {number} hotelTotalIls
 * @param {object|null|undefined} lockedSnap
 * @param {{
 *   currencyRate?: object|null,
 *   adminUsdRate?: number|null,
 *   adminEurRate?: number|null,
 *   adminGbpRate?: number|null,
 *   profilePriceContext?: object|null,
 *   usdToIls?: ((usd: number, fixed?: boolean) => number)|null,
 * }} [rateOpts]
 */
export function enrichHotelLockedFxFields(
  order,
  hotelTotalIls,
  lockedSnap = null,
  rateOpts = {}
) {
  const fxRateOpts = buildHotelLockedFxRateOpts(rateOpts);
  const pkg =
    order?.selectedHotelPackage && typeof order.selectedHotelPackage === "object"
      ? order.selectedHotelPackage
      : {};
  const dc = resolveOrderDisplayCurrencies(pkg);
  const usdAmount = parseDisplayCurrencyAmount(dc, "USD");
  const eurAmount = parseDisplayCurrencyAmount(dc, "EUR");

  let supplierCurrency = lockedSnap?.supplierCurrency
    ? normalizeSupplierCurrencyForBooking(lockedSnap.supplierCurrency)
    : null;

  if (!supplierCurrency || supplierCurrency === "ILS") {
    if (usdAmount != null && usdAmount > 0) {
      supplierCurrency = "USD";
    } else if (eurAmount != null && eurAmount > 0) {
      supplierCurrency = "EUR";
    } else if (dc?.hasUsd === true) {
      supplierCurrency = "USD";
    } else {
      supplierCurrency = normalizeSupplierCurrencyForBooking(
        pkg?.priceCurrency ||
          pkg?.PriceCurrency ||
          pkg?.ourPrice?.originalCurrency ||
          order?.originalCurrency ||
          "USD"
      );
    }
  }

  let hotelTotalOriginalCurrency =
    lockedSnap?.hotelTotalOriginalCurrency != null &&
    Number.isFinite(Number(lockedSnap.hotelTotalOriginalCurrency))
      ? Number(lockedSnap.hotelTotalOriginalCurrency)
      : null;
  let lockedExchangeRateSource = lockedSnap?.lockedExchangeRateSource ?? null;
  const lockedExchangeRateAt = lockedSnap?.lockedExchangeRateAt ?? null;

  const ils = roundIls(hotelTotalIls);
  if (supplierCurrency !== "ILS" && ils > 0) {
    const dcAmount =
      supplierCurrency === "USD"
        ? usdAmount
        : supplierCurrency === "EUR"
          ? eurAmount
          : null;

    if (
      (hotelTotalOriginalCurrency == null || hotelTotalOriginalCurrency <= 0) &&
      dcAmount != null &&
      Number.isFinite(dcAmount) &&
      dcAmount > 0
    ) {
      hotelTotalOriginalCurrency = dcAmount;
    }
  }

  const adminRate = resolveAdminHotelLockedExchangeRateToIls(
    supplierCurrency,
    fxRateOpts
  );
  let lockedExchangeRateToIls = adminRate;
  if (adminRate != null) {
    lockedExchangeRateSource = "admin_currency_rate";
  }

  if (supplierCurrency === "ILS") {
    lockedExchangeRateToIls = 1;
    hotelTotalOriginalCurrency = ils;
    lockedExchangeRateSource = lockedExchangeRateSource ?? "ILS_NATIVE";
  }

  return {
    supplierCurrency,
    lockedExchangeRateToIls,
    hotelTotalOriginalCurrency,
    lockedExchangeRateAt,
    lockedExchangeRateSource,
  };
}

/**
 * @param {object} order
 * @param {{
 *   usdToIls?: (n: number) => number,
 *   profilePriceContext?: object|null,
 *   currencyRate?: object|null,
 *   adminUsdRate?: number|null,
 * }} [opts]
 * @returns {object}
 */
export function ensureHotelOrderPricingSnapshotFx(order, opts = {}) {
  if (!order || typeof order !== "object") return order;

  const hotelTotalIls =
    pickSnapshot(order)?.hotelTotalIls != null
      ? roundIls(pickSnapshot(order).hotelTotalIls)
      : resolveCustomerFacingHotelLineIlsFromOrder(order);
  if (!hotelTotalIls) return order;

  const existing = pickSnapshot(order);
  const fx = enrichHotelLockedFxFields(order, hotelTotalIls, existing, opts);
  if (
    fx.supplierCurrency === "ILS" ||
    !fx.lockedExchangeRateToIls ||
    fx.lockedExchangeRateToIls <= 0
  ) {
    return order;
  }

  if (
    existing?.lockedExchangeRateToIls != null &&
    existing?.supplierCurrency === fx.supplierCurrency &&
    existing?.hotelTotalOriginalCurrency != null
  ) {
    return order;
  }

  const agentFeeIls = Math.max(0, Math.round(Number(existing?.agentFeeIls) || 0));
  const nextSnap = {
    ...(existing || {}),
    supplierCurrency: fx.supplierCurrency,
    hotelTotalOriginalCurrency: fx.hotelTotalOriginalCurrency,
    lockedExchangeRateToIls: fx.lockedExchangeRateToIls,
    lockedExchangeRateAt: fx.lockedExchangeRateAt ?? new Date().toISOString(),
    lockedExchangeRateSource:
      fx.lockedExchangeRateSource ?? "ensureHotelOrderPricingSnapshotFx",
    hotelTotalIls,
    agentFeeIls,
    totalIls: hotelTotalIls + agentFeeIls,
  };

  logHotelLockedFxRateTrace({
    ...nextSnap,
    sourcePath: "ensureHotelOrderPricingSnapshotFx",
  });

  const selectedPriceSnapshot =
    order.selectedPriceSnapshot && typeof order.selectedPriceSnapshot === "object"
      ? {
          ...order.selectedPriceSnapshot,
          finalPriceIls: order.selectedPriceSnapshot.finalPriceIls ?? hotelTotalIls,
          pricingSnapshot: nextSnap,
        }
      : {
          finalPriceIls: hotelTotalIls,
          pricingSnapshot: nextSnap,
        };

  return {
    ...order,
    pricingSnapshot: nextSnap,
    selectedPriceSnapshot,
  };
}

/**
 * נעילת שער + מחירון בסיס ברגע בחירת חדר / שריון.
 * @returns {Record<string, unknown>|null}
 */
export function buildHotelBookingPricingSnapshotAtLock({
  selectedPackage,
  profilePriceContext,
  bookingSsotIls,
  bookingSsotTrace = null,
}) {
  const hotelTotalIls = roundBookingSsotIlsForSnapshot(bookingSsotIls, bookingSsotTrace);
  if (hotelTotalIls <= 0) return null;

  const pkg = selectedPackage;
  const hotelInfo = profilePriceContext?.hotelInfo ?? null;
  const supplierCurrency = normalizeSupplierCurrencyForBooking(
    pkg?.priceCurrency ||
      pkg?.PriceCurrency ||
      pkg?.ourPrice?.originalCurrency ||
      hotelInfo?.Currency ||
      hotelInfo?.currency ||
      resolveHotelProfilePackageRawCurrency(pkg, hotelInfo, profilePriceContext) ||
      "USD"
  );
  const supplierAmount = getHotelProfilePackageSupplierUsd(pkg);
  const displayCurrencies =
    pkg?.displayCurrencies && typeof pkg.displayCurrencies === "object"
      ? { ...resolveHotelPackageDisplayCurrencies(pkg), ...pkg.displayCurrencies }
      : resolveHotelPackageDisplayCurrencies(pkg);

  let lockedExchangeRateToIls = 1;
  let lockedExchangeRateSource = "ILS_NATIVE";
  let hotelTotalOriginalCurrency = hotelTotalIls;

  if (supplierCurrency && supplierCurrency !== "ILS") {
    const adminRate = resolveAdminHotelLockedExchangeRateToIls(supplierCurrency, {
      profilePriceContext,
      currencyRate: profilePriceContext?.currencyRate ?? null,
      adminUsdRate: profilePriceContext?.usdToIlsRate ?? null,
      adminEurRate: profilePriceContext?.eurToIls ?? null,
      adminGbpRate: profilePriceContext?.gbpToIls ?? null,
      usdToIls: profilePriceContext?.usdToIls ?? null,
    });
    if (adminRate != null) {
      lockedExchangeRateToIls = adminRate;
      lockedExchangeRateSource = "admin_currency_rate";
    }

    const dcAmount =
      supplierCurrency === "USD"
        ? displayCurrencies?.usd
        : supplierCurrency === "EUR"
          ? displayCurrencies?.eur ?? displayCurrencies?.EUR
          : null;
    if (dcAmount != null && Number.isFinite(Number(dcAmount)) && Number(dcAmount) > 0) {
      hotelTotalOriginalCurrency = Number(dcAmount);
      if (adminRate == null) {
        lockedExchangeRateSource = `${lockedExchangeRateSource}|displayCurrencies_no_admin_rate`;
      }
    } else if (adminRate != null && adminRate > 0) {
      hotelTotalOriginalCurrency = hotelTotalIls / adminRate;
    }
  }

  let hrCommissionOriginalCurrency = 0;
  if (
    bookingSsotTrace?.hrCommission != null &&
    Number.isFinite(Number(bookingSsotTrace.hrCommission))
  ) {
    hrCommissionOriginalCurrency = Number(bookingSsotTrace.hrCommission);
  } else if (
    supplierAmount != null &&
    Number.isFinite(Number(supplierAmount)) &&
    Number.isFinite(Number(hotelTotalOriginalCurrency))
  ) {
    hrCommissionOriginalCurrency = Math.max(
      0,
      Number(hotelTotalOriginalCurrency) - Number(supplierAmount)
    );
  }

  const tracePipeline =
    bookingSsotTrace?.pipeline ?? bookingSsotTrace?.source ?? null;

  const snapshot = {
    supplierCurrency,
    supplierAmount:
      supplierAmount != null && Number.isFinite(Number(supplierAmount))
        ? Number(supplierAmount)
        : null,
    hrCommissionOriginalCurrency,
    hotelTotalOriginalCurrency: Number.isFinite(Number(hotelTotalOriginalCurrency))
      ? Number(hotelTotalOriginalCurrency)
      : null,
    lockedExchangeRateToIls,
    lockedExchangeRateAt: new Date().toISOString(),
    lockedExchangeRateSource: tracePipeline
      ? `${lockedExchangeRateSource}|${tracePipeline}`
      : lockedExchangeRateSource,
    hotelTotalIls,
    agentFeeIls: 0,
    totalIls: hotelTotalIls,
  };

  logHotelLockedFxRateTrace({
    ...snapshot,
    sourcePath: "buildHotelBookingPricingSnapshotAtLock",
  });

  return snapshot;
}

/**
 * @param {object} order
 * @param {number} agentFeeIls
 * @returns {object}
 */
export function applyAgentFeeToHotelPricingSnapshot(order, agentFeeIls) {
  const fee = Math.max(0, Math.round(Number(agentFeeIls) || 0));
  let baseSnap = pickSnapshot(order);
  const stableHotelTotalIls = roundIls(
    order?.bookingSsotIls ??
      order?.selectedPackageOriginalSnapshot?.pricingSnapshot?.hotelTotalIls ??
      order?.selectedPackageOriginalSnapshot?.finalPriceIls ??
      order?.selectedPriceSnapshot?.pricingSnapshot?.hotelTotalIls ??
      order?.selectedPriceSnapshot?.finalPriceIls ??
      baseSnap?.hotelTotalIls ??
      0
  );
  if (!baseSnap) {
    const hotelTotalIls = stableHotelTotalIls;
    if (hotelTotalIls <= 0) return order;
    baseSnap = {
      supplierCurrency: "ILS",
      hotelTotalIls,
      agentFeeIls: 0,
      totalIls: hotelTotalIls,
      lockedExchangeRateToIls: 1,
      lockedExchangeRateSource: "applyAgentFee.bootstrap",
    };
  }

  const hotelTotalIls = stableHotelTotalIls > 0 ? stableHotelTotalIls : roundIls(baseSnap.hotelTotalIls);
  const nextSnap = {
    ...baseSnap,
    hotelTotalIls,
    agentFeeIls: fee,
    totalIls: hotelTotalIls + fee,
  };

  const selectedPriceSnapshot =
    order.selectedPriceSnapshot && typeof order.selectedPriceSnapshot === "object"
      ? { ...order.selectedPriceSnapshot, pricingSnapshot: nextSnap }
      : order.selectedPriceSnapshot;

  return {
    ...order,
    pricingSnapshot: nextSnap,
    selectedPriceSnapshot,
  };
}

/**
 * @param {Record<string, unknown>} payload
 */
export function logHotelBookingSummaryPriceSsotTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_BOOKING_SUMMARY_PRICE_SSOT_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>} payload
 */
export function logHotelAgentFeeSummaryTotalTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_AGENT_FEE_SUMMARY_TOTAL_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>} payload
 */
export function logHotelLockedFxRateTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    const displayedIls = payload.hotelTotalIls ?? payload.displayedIls ?? null;
    const supplierUsd =
      payload.supplierUsd ??
      payload.supplierAmount ??
      payload.hotelTotalOriginalCurrency ??
      null;
    const adminRate = payload.adminRate ?? payload.lockedExchangeRateToIls ?? null;
    const impliedRate =
      payload.impliedRate ??
      computeImpliedHotelFxRate(displayedIls, supplierUsd);
    // eslint-disable-next-line no-console
    console.info(HOTEL_LOCKED_FX_RATE_TRACE, {
      supplierCurrency: payload.supplierCurrency ?? null,
      lockedExchangeRateToIls: payload.lockedExchangeRateToIls ?? null,
      lockedExchangeRateAt: payload.lockedExchangeRateAt ?? null,
      lockedExchangeRateSource: payload.lockedExchangeRateSource ?? null,
      hotelTotalOriginalCurrency: payload.hotelTotalOriginalCurrency ?? null,
      hotelTotalIls: displayedIls,
      totalIls: payload.totalIls ?? null,
      sourcePath: payload.sourcePath ?? null,
      adminRate,
      supplierUsd,
      displayedIls,
      impliedRate,
      selectedRateSource:
        payload.selectedRateSource ??
        (adminRate != null ? "admin_currency_rate" : null),
    });
  } catch {
    /* ignore */
  }
}

/**
 * המרת USD→ILS לפי שער נעול ב-pricingSnapshot (לא שער קטלוג חי).
 * @param {object|null|undefined} pricingSnapshot
 * @param {(usd: number, fixed?: boolean) => number} [liveUsdToIls]
 * @returns {(usd: number) => number}
 */
export function buildLockedUsdToIlsFn(pricingSnapshot, liveUsdToIls) {
  const snap = pricingSnapshot && typeof pricingSnapshot === "object" ? pricingSnapshot : null;
  const cur = normalizeSupplierCurrencyForBooking(snap?.supplierCurrency);
  const rate = roundRate(snap?.lockedExchangeRateToIls);
  if (cur === "USD" && rate != null && rate > 0) {
    return (usd) => {
      const n = Number(usd);
      if (!Number.isFinite(n)) return 0;
      return Math.ceil(n * rate);
    };
  }
  if (typeof liveUsdToIls === "function") {
    return (usd) => liveUsdToIls(usd);
  }
  return (usd) => Number(usd) || 0;
}

/**
 * displayCurrencies לתצוגה כפולה — מסכומים שננעלו ב-pricingSnapshot (לא שער חי).
 * @param {object|null|undefined} pricingSnapshot
 * @param {object|null|undefined} displayCurrenciesFallback
 */
export function buildLockedHotelDisplayCurrenciesFromSnapshot(
  pricingSnapshot,
  displayCurrenciesFallback = null
) {
  const snap = pricingSnapshot && typeof pricingSnapshot === "object" ? pricingSnapshot : null;
  if (!snap) return displayCurrenciesFallback ?? null;

  const cur = normalizeSupplierCurrencyForBooking(snap.supplierCurrency);
  if (cur === "ILS") return displayCurrenciesFallback ?? null;

  const original = snap.hotelTotalOriginalCurrency;
  if (original == null || !Number.isFinite(Number(original)) || Number(original) <= 0) {
    return displayCurrenciesFallback ?? null;
  }

  const formattedOriginal = formatHotelSearchAmountForDisplay(original, cur);
  const base = displayCurrenciesFallback && typeof displayCurrenciesFallback === "object"
    ? { ...displayCurrenciesFallback }
    : {};

  if (cur === "USD") {
    const formattedUsd =
      formattedOriginal != null
        ? formattedOriginal.startsWith("(")
          ? formattedOriginal
          : `(${formattedOriginal})`
        : null;
    return {
      ...base,
      usd: Number(original),
      formattedUsd,
      hasUsd: formattedUsd != null,
      lockedExchangeRateToIls: snap.lockedExchangeRateToIls ?? null,
      lockedExchangeRateAt: snap.lockedExchangeRateAt ?? null,
      lockedExchangeRateSource: snap.lockedExchangeRateSource ?? null,
    };
  }

  if (cur === "EUR") {
    return {
      ...base,
      eur: Number(original),
      formattedEur: formattedOriginal,
      hasEur: true,
      lockedExchangeRateToIls: snap.lockedExchangeRateToIls ?? null,
      lockedExchangeRateAt: snap.lockedExchangeRateAt ?? null,
    };
  }

  return {
    ...base,
    originalAmount: Number(original),
    originalCurrency: cur,
    formattedOriginal,
    lockedExchangeRateToIls: snap.lockedExchangeRateToIls ?? null,
    lockedExchangeRateAt: snap.lockedExchangeRateAt ?? null,
  };
}

/**
 * displayCurrencies לסכום חיוב מלא (דמי ביטול = סה״כ כולל דמי טיפול).
 * @param {number} totalIls
 * @param {object|null|undefined} pricingSnapshot
 * @param {object|null|undefined} fallbackDc
 */
export function buildDisplayCurrenciesForChargeTotalIls(
  totalIls,
  pricingSnapshot,
  fallbackDc = null
) {
  const ils = roundIls(totalIls);
  if (ils <= 0) return fallbackDc ?? null;

  const snap = pricingSnapshot && typeof pricingSnapshot === "object" ? pricingSnapshot : null;
  const base =
    fallbackDc && typeof fallbackDc === "object" ? { ...fallbackDc } : {};
  const rate = roundRate(snap?.lockedExchangeRateToIls);
  const cur = snap?.supplierCurrency
    ? normalizeSupplierCurrencyForBooking(snap.supplierCurrency)
    : null;

  const formatUsdDc = (usd) => {
    const formattedOriginal = formatHotelSearchAmountForDisplay(usd, "USD");
    if (!formattedOriginal) return null;
    const formattedUsd = formattedOriginal.startsWith("(")
      ? formattedOriginal
      : `(${formattedOriginal})`;
    return {
      ...base,
      ils,
      usd: Math.round(Number(usd) * 100) / 100,
      formattedUsd,
      hasUsd: true,
    };
  };

  if (rate && cur === "USD") {
    const out = formatUsdDc(ils / rate);
    if (out) return out;
  }

  const hotelIls = roundIls(snap?.hotelTotalIls);
  const hotelUsd = parseDisplayCurrencyAmount(fallbackDc, "USD");
  if (hotelUsd && hotelIls > 0) {
    const out = formatUsdDc((hotelUsd * ils) / hotelIls);
    if (out) return out;
  }

  const locked = buildLockedHotelDisplayCurrenciesFromSnapshot(snap, fallbackDc);
  if (locked?.usd != null && hotelIls > 0 && ils !== hotelIls) {
    const out = formatUsdDc((Number(locked.usd) * ils) / hotelIls);
    if (out) return out;
  }

  if (locked) {
    return { ...locked, ils };
  }

  return {
    ...base,
    ils,
    formattedIls: formatHotelIlsRoundedDisplay(ils),
    hasUsd: base.hasUsd === true && Boolean(base.formattedUsd),
  };
}

/**
 * סכום דמי ביטול להצגה — תמיד סה״כ כולל דמי טיפול (+ תוספות מקושרות אם הועברו).
 * @param {{
 *   currentHotelOrder: object|null,
 *   calcOurTotalPrice?: (order: object) => object|null,
 *   usdToIls?: (usd: number, fixed?: boolean) => number,
 *   linkedFlightsDealPrice?: number,
 *   supplementsIls?: number,
 * }} opts
 */
export function resolveHotelCancellationChargeDisplay(opts) {
  const order = opts.currentHotelOrder;
  const ssot = resolveHotelBookingSummaryPricingSsot({
    currentHotelOrder: order,
    calcOurTotalPrice: opts.calcOurTotalPrice,
    usdToIls: opts.usdToIls,
    trace: false,
  });
  const extras = Math.ceil(
    (Number(opts.linkedFlightsDealPrice) || 0) + (Number(opts.supplementsIls) || 0)
  );
  const chargeIls =
    ssot.ok && Number(ssot.totalWithAgentFeeIls) > 0
      ? roundIls(ssot.totalWithAgentFeeIls) + extras
      : 0;
  const fallbackDc = order?.selectedHotelPackage?.displayCurrencies;
  const displayCurrencies =
    chargeIls > 0
      ? buildDisplayCurrenciesForChargeTotalIls(
          chargeIls,
          ssot.pricingSnapshot,
          fallbackDc
        )
      : fallbackDc ?? null;

  return { chargeIls, displayCurrencies, ssot };
}

/**
 * @param {string} supplierCurrency
 * @param {number|null} rate
 */
export function formatLockedExchangeRateLabel(supplierCurrency, rate) {
  const cur = normalizeSupplierCurrencyForBooking(supplierCurrency) || "USD";
  const r = roundRate(rate);
  if (cur === "ILS" || r == null) return null;
  return `1 ${cur} = ₪${r.toFixed(4)}`;
}

/**
 * @param {{
 *   currentHotelOrder: object|null,
 *   calcOurTotalPrice?: (order: object) => object|null,
 *   usdToIls?: (usd: number, fixed?: boolean) => number,
 *   currencyRate?: { USD?: number, EUR?: number, GBP?: number }|null,
 *   adminUsdRate?: number|null,
 *   adminEurRate?: number|null,
 *   adminGbpRate?: number|null,
 *   profilePriceContext?: object|null,
 *   orderReqData?: object|null,
 *   priceCalculations?: object|null,
 *   orderPayments?: Array<object>|null,
 *   sourcePath?: string,
 *   trace?: boolean,
 * }} opts
 */
export function resolveHotelBookingSummaryPricingSsot(opts) {
  const order = opts.currentHotelOrder;
  const sourcePath = opts.sourcePath ?? "unknown";
  const shouldTrace = opts.trace !== false;

  if (!order) {
    return {
      ok: false,
      hotelTotalIls: 0,
      agentFeeIls: 0,
      totalIls: 0,
      totalWithCreditCardIls: 0,
      feeIncludedInTotal: false,
      sourcePath,
    };
  }

  const lockedSnapRaw = pickSnapshot(order);
  const hotelTotalIls = resolveSummaryHotelTotalIlsFromOrder(order);

  const fxRateOpts = buildHotelLockedFxRateOpts(opts);
  const fx = enrichHotelLockedFxFields(
    order,
    hotelTotalIls,
    lockedSnapRaw,
    fxRateOpts
  );
  const lockedSnap = lockedSnapRaw
    ? {
        ...lockedSnapRaw,
        supplierCurrency: fx.supplierCurrency ?? lockedSnapRaw.supplierCurrency,
        hotelTotalOriginalCurrency:
          fx.hotelTotalOriginalCurrency ?? lockedSnapRaw.hotelTotalOriginalCurrency,
        lockedExchangeRateAt: lockedSnapRaw.lockedExchangeRateAt ?? fx.lockedExchangeRateAt,
        lockedExchangeRateSource:
          lockedSnapRaw.lockedExchangeRateSource ?? fx.lockedExchangeRateSource,
        hotelTotalIls,
      }
    : hotelTotalIls > 0 && fx.supplierCurrency && fx.supplierCurrency !== "ILS"
      ? {
          supplierCurrency: fx.supplierCurrency,
          lockedExchangeRateToIls: fx.lockedExchangeRateToIls,
          hotelTotalOriginalCurrency: fx.hotelTotalOriginalCurrency,
          lockedExchangeRateAt: fx.lockedExchangeRateAt ?? new Date().toISOString(),
          lockedExchangeRateSource:
            fx.lockedExchangeRateSource ?? "enriched_displayCurrencies_at_read",
          hotelTotalIls,
          agentFeeIls: 0,
          totalIls: hotelTotalIls,
        }
      : null;

  const ourPrice = order.ourPrice || order.selectedHotelPackage?.ourPrice || {};
  const lockedUsdToIls = buildLockedUsdToIlsFn(lockedSnap, opts.usdToIls);
  const fallbackAgentFeeIls = resolveHotelAgentHandlingFeeIls(
    ourPrice,
    hotelTotalIls,
    lockedUsdToIls
  );
  const handlingFeeSsot = resolveHotelHandlingFeeSnapshotSsot({
    order,
    orderReqData: opts.orderReqData,
    priceCalculations: opts.priceCalculations,
    orderPayments: opts.orderPayments,
    bookingExchangeRate: lockedSnap?.lockedExchangeRateToIls ?? fx.lockedExchangeRateToIls,
    fallbackAgentFeeIls,
  });
  const agentFeeIls = handlingFeeSsot.feeIlsSnapshot;

  const totalWithAgentFeeIls = hotelTotalIls + agentFeeIls;
  const totalIls = totalWithAgentFeeIls;
  const calcPrice =
    typeof opts.calcOurTotalPrice === "function"
      ? opts.calcOurTotalPrice(order)
      : null;
  const totalWithCreditCardIls = roundIls(
    calcPrice?.priceWithCreditCardFee ?? calcPrice?.customerDisplayTotalIls ?? totalIls
  );

  const supplierCurrency =
    fx.supplierCurrency ??
    lockedSnap?.supplierCurrency ??
    normalizeSupplierCurrencyForBooking(order.originalCurrency) ??
    "USD";
  const supplierAmount = lockedSnap?.supplierAmount ?? null;
  const hrCommissionOriginalCurrency =
    lockedSnap?.hrCommissionOriginalCurrency ?? null;
  const hotelTotalOriginalCurrency =
    fx.hotelTotalOriginalCurrency ?? lockedSnap?.hotelTotalOriginalCurrency ?? null;
  const lockedExchangeRateToIls = fx.lockedExchangeRateToIls ?? null;
  const lockedExchangeRateAt =
    lockedSnap?.lockedExchangeRateAt ?? fx.lockedExchangeRateAt ?? null;

  const pkg =
    order.selectedHotelPackage && typeof order.selectedHotelPackage === "object"
      ? order.selectedHotelPackage
      : {};
  const hotelId =
    order.selectedHotel?.HotelId ??
    order.selectedHotel?.hotelId ??
    order.searchPhrase?.hotelID ??
    null;
  const packageId =
    pkg.packageID ?? pkg.PackageId ?? pkg.packageId ?? null;
  const roomName =
    order.selectedRoom?.roomNames?.[0] ??
    order.selectedRoom?.name ??
    pkg.roomNames?.[0] ??
    null;

  const supplierFormatted =
    supplierAmount != null && supplierCurrency
      ? formatHotelSearchAmountForDisplay(supplierAmount, supplierCurrency)
      : null;
  const hotelOriginalFormatted =
    hotelTotalOriginalCurrency != null && supplierCurrency
      ? formatHotelSearchAmountForDisplay(
          hotelTotalOriginalCurrency,
          supplierCurrency
        )
      : null;
  const hotelIlsFormatted = formatHotelIlsRoundedDisplay(hotelTotalIls);
  const agentFeeFormatted =
    handlingFeeSsot.hasSnapshot && handlingFeeSsot.displayFormatted !== "—"
      ? handlingFeeSsot.displayFormatted
      : formatHotelIlsRoundedDisplay(agentFeeIls);
  const totalFormatted = formatHotelIlsRoundedDisplay(totalWithAgentFeeIls);
  const totalWithAgentFeeFormatted = totalFormatted;
  const exchangeRateLabel = formatLockedExchangeRateLabel(
    supplierCurrency,
    lockedExchangeRateToIls
  );

  const result = {
    ok: hotelTotalIls > 0,
    pricingSnapshot: lockedSnap,
    supplierCurrency,
    supplierAmount,
    hrCommissionOriginalCurrency,
    hotelTotalOriginalCurrency,
    lockedExchangeRateToIls,
    lockedExchangeRateAt,
    lockedExchangeRateSource: lockedSnap?.lockedExchangeRateSource ?? null,
    hotelTotalIls,
    agentFeeIls,
    agentFeeUsd: handlingFeeSsot.feeUsd,
    handlingFeeSsot,
    totalWithAgentFeeIls,
    totalIls,
    totalWithCreditCardIls,
    displayedTotalIls: totalWithCreditCardIls,
    displayedHotelTotal: hotelIlsFormatted,
    displayedFinalTotal: totalWithAgentFeeFormatted,
    totalWithAgentFeeFormatted,
    feeIncludedInTotal: agentFeeIls > 0,
    supplierFormatted,
    hotelOriginalFormatted,
    hotelIlsFormatted,
    agentFeeFormatted,
    totalFormatted,
    exchangeRateLabel,
    isApiPriceOnly: isHotelApiPriceOnlyOrder(order),
    hotelId,
    packageId,
    roomName,
    sourcePath,
  };

  if (shouldTrace) {
    logHotelBookingSummaryPriceSsotTrace({
      hotelId,
      packageId,
      roomName,
      supplierCurrency,
      supplierAmount,
      hrCommissionOriginalCurrency,
      hotelTotalOriginalCurrency,
      lockedExchangeRateToIls,
      lockedExchangeRateAt,
      hotelTotalIls,
      agentFeeIls,
      totalIls,
      displayedTotalIls: totalWithCreditCardIls,
      feeIncludedInTotal: agentFeeIls > 0,
      sourcePath,
    });
    logHotelAgentFeeSummaryTotalTrace({
      hotelTotalIls,
      agentFeeIls,
      totalWithAgentFeeIls,
      displayedHotelTotal: hotelIlsFormatted,
      displayedFinalTotal: totalWithAgentFeeFormatted,
      sourcePath,
    });
    logHotelLockedFxRateTrace({
      supplierCurrency,
      lockedExchangeRateToIls,
      lockedExchangeRateAt,
      lockedExchangeRateSource:
        lockedSnap?.lockedExchangeRateSource ?? fx.lockedExchangeRateSource ?? null,
      hotelTotalOriginalCurrency,
      hotelTotalIls,
      totalIls,
      sourcePath,
      adminRate: lockedExchangeRateToIls,
      supplierUsd: supplierAmount ?? hotelTotalOriginalCurrency,
      displayedIls: hotelTotalIls,
      impliedRate: computeImpliedHotelFxRate(
        hotelTotalIls,
        hotelTotalOriginalCurrency ?? supplierAmount
      ),
      selectedRateSource:
        fx.lockedExchangeRateSource === "admin_currency_rate"
          ? "admin_currency_rate"
          : null,
      enrichedFromDisplayCurrencies: !lockedSnapRaw?.lockedExchangeRateToIls,
    });
  }

  return result;
}
